/**
 * Hook `useKPIForm`
 * 
 * Este hook proporciona las funcionalidades relacionadas con el manejo de formularios KPI (Indicadores Clave de Desempeño),
 * incluyendo la carga de preguntas, filtros, actualización de campos y manejo de estados del formulario.
 *
 * @returns {object} Un objeto que contiene el estado del formulario KPI y las funciones para manejarlo.
 */

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import {
  setKPIForm,
  setField,
  setFilter,
  resetFilters,
  resetKPIFormFields
} from 'src/redux/Dashboard/KPIForm/KPIFormSlice'
import {
  getFilters,
  updateAnswers,
} from 'src/services/KPI/KPIForm/KPIForm.service'
import { getQuestiosForm } from 'src/services/KPI/Questions/Questions.service'
import {
  IFilter,
  IKPIFormField,
} from 'src/redux/Dashboard/KPIForm/KPIForm.interface'
import { IParamsUpdateAnswers } from 'src/services/KPI/KPIForm/KPIForm.interface'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'

export const useKPIForm = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { kpiFormList, filters, filter } = useSelector(
    (state: RootState) => state.kpiForm,
  )

  /**
   * Carga las preguntas para el formulario KPI basadas en el filtro actual.
   * Muestra alertas según el resultado de la carga.
   * 
   * @function loadQuestions
   * @throws {Error} Si ocurre un error inesperado al cargar las preguntas.
   */
  const loadQuestions = () => {
    try {
      restarKPIFormFields()
      startLoading()
      dispatch(getQuestiosForm(filter))
        .then((result: any) => {
          stopLoading()
          if (getQuestiosForm.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las preguntas',
            )
          } else if (getQuestiosForm.fulfilled.match(result)) {
            showAlert('success', 'Preguntas cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Carga los filtros disponibles para el formulario KPI.
   * Muestra alertas según el resultado de la carga.
   * 
   * @function loadFilters
   * @param {object} params - Parámetros para la carga de filtros.
   * @param {boolean} [params.includeGeneral=false] - Indica si se deben incluir filtros generales.
   * @throws {Error} Si ocurre un error inesperado al cargar los filtros.
   */
  const loadFilters = (
    { includeGeneral }: { includeGeneral?: boolean } = {
      includeGeneral: false,
    },
  ) => {
    try {
      startLoading()
      dispatch(
        getFilters({
          includeGeneral,
        }),
      )
      showAlert('success', 'Filtros cargados correctamente')
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    } finally {
      stopLoading()
    }
  }

  /**
   * Reinicia los campos del formulario KPI.
   * 
   * @function restarKPIFormFields
   */
  const restarKPIFormFields = () => {
    dispatch(resetKPIFormFields())
  }

  /**
   * Establece los filtros seleccionados en el formulario KPI.
   * 
   * @function setFilterForm
   * @param {IFilter} filters - Filtros que se aplicarán al formulario.
   */
  const setFilterForm = (filters: IFilter) => {
    dispatch(setFilter(filters))
  }

  /**
   * Actualiza las respuestas del formulario KPI.
   * 
   * @function setAnswers
   * @param {IParamsUpdateAnswers[]} answers - Respuestas que se actualizarán en el formulario.
   */
  const setAnswers = (answers: IParamsUpdateAnswers[]) => {
    dispatch(updateAnswers(answers))
  }

  /**
   * Establece el estado del formulario KPI.
   * 
   * @function setKPIFormState
   * @param {IKPIFormField} kpiForm - Datos del formulario KPI.
   */
  const setKPIFormState = (kpiForm: IKPIFormField) => {
    dispatch(setKPIForm(kpiForm))
  }

  /**
   * Actualiza un campo específico del formulario KPI.
   * 
   * @function setFieldState
   * @param {Partial<IKPIFormField>} field - Campo que se actualizará.
   */
  const setFieldState = (field: Partial<IKPIFormField>) => {
    dispatch(setField(field))
  }

  /**
   * Reinicia los filtros aplicados en el formulario KPI.
   * 
   * @function resetFiltersFields
   */
  const resetFiltersFields = () => {
    dispatch(resetFilters())
  }

  return {
    kpiFormList,
    filters,
    filter,
    setFilterForm,
    setAnswers,
    setKPIFormState,
    setFieldState,
    loadQuestions,
    loadFilters,
    resetFiltersFields,
    restarKPIFormFields
  }
}

export default useKPIForm
